import React, { useEffect } from 'react';
import { Clock, FileText, Heart } from 'lucide-react';
import Icon from '../AppIcon';
import { resolveHubupAssetUrl } from '../../api/hubup';
import {
  HUBUP_NOTIF_FEED_SIZE_MD,
  notificationFeedBrandStyles,
  sanitizeNotificationDisplayText,
  sanitizeRichBodyHtmlForPreview,
} from '../../lib/hubupNotificationFeedBrand';

const FORM_PICTO_SRC = '/assets/form-picto.gif';

const GENERIC_FONTS = new Set([
  'sans-serif',
  'serif',
  'monospace',
  'cursive',
  'system-ui',
  'ui-sans-serif',
  'ui-serif',
]);

function primaryFontFamily(font) {
  const t = String(font || '').trim();
  if (!t) return null;
  const first = t.split(',')[0]?.trim() ?? '';
  const name = first.replace(/^["']|["']$/g, '');
  if (!name || GENERIC_FONTS.has(name.toLowerCase())) return null;
  return name;
}

function HubupGroupFeedFontLoader({ font }) {
  useEffect(() => {
    const family = font ? primaryFontFamily(font) : null;
    if (!family) return undefined;
    const id = `hubup-gf-${family.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(id)) return undefined;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      family,
    )}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
    return undefined;
  }, [font]);
  return null;
}

function FeedTopicAvatar({
  groupIconUrl,
  topicIconUrl,
  feedIcon = '📢',
  size = 'md',
}) {
  const dim =
    size === 'lg'
      ? 'w-12 h-12 text-2xl rounded-2xl'
      : 'w-10 h-10 text-xl rounded-2xl';
  const topicRaw = typeof topicIconUrl === 'string' ? topicIconUrl.trim() : '';
  const groupSrc = resolveHubupAssetUrl(groupIconUrl);

  if (topicRaw && (topicRaw.startsWith('/') || /^https?:\/\//i.test(topicRaw))) {
    const src = topicRaw.startsWith('http')
      ? topicRaw
      : `${window.location.origin}${topicRaw}`;
    return (
      <div
        className={`flex-shrink-0 ${dim} overflow-hidden shadow-sm border border-[#E8E4FF] bg-white`}
      >
        <img src={src} alt="" className="h-full w-full object-contain bg-white p-0.5" />
      </div>
    );
  }

  if (groupSrc) {
    return (
      <div
        className={`flex-shrink-0 ${dim} overflow-hidden shadow-sm border border-[#E8E4FF] bg-white`}
      >
        <img src={groupSrc} alt="" className="h-full w-full object-cover" />
      </div>
    );
  }

  return (
    <div
      className={`flex-shrink-0 ${dim} flex items-center justify-center shadow-sm border border-[#E8E4FF] bg-transparent`}
    >
      {feedIcon}
    </div>
  );
}

function FeedFormAvatar({ size = 'md' }) {
  const dim = size === 'lg' ? 'w-12 h-12 rounded-2xl' : 'w-10 h-10 rounded-2xl';
  return (
    <div
      className={`flex shrink-0 overflow-hidden border border-[#E8E4FF] bg-white shadow-sm ${dim}`}
      aria-hidden
    >
      <img
        src={FORM_PICTO_SRC}
        alt=""
        className="h-full w-full object-contain bg-white p-0.5"
      />
    </div>
  );
}

function ProVerifiedBadge({ size = 14 }) {
  return (
    <span className="inline-flex shrink-0 items-center" title="Professionel Vérifié">
      <Icon
        name="BadgeCheck"
        size={size}
        className="fill-[#F5C542] text-[#B8860B]"
        strokeWidth={2.25}
      />
      <span className="sr-only">Professionel Vérifié</span>
    </span>
  );
}

function NotificationPdfCard({ label, compact }) {
  return (
    <div
      className={`flex w-full min-w-0 items-center gap-3 rounded-md border border-[#E8E4FF] bg-[#F7F6F0]/90 px-3 shadow-sm ${
        compact ? 'min-h-[7rem] py-3' : 'min-h-[4.5rem] py-2.5'
      }`}
    >
      <span
        className={`flex shrink-0 items-center justify-center rounded-lg bg-[#FEE2E2] text-[#B91C1C] ${
          compact ? 'h-12 w-12' : 'h-10 w-10'
        }`}
        aria-hidden
      >
        <FileText size={compact ? 24 : 20} />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-sm font-bold text-[#1E1B4B]">{label}</span>
        <span className="mt-0.5 block text-xs font-medium text-[#333483]">PDF</span>
      </span>
    </div>
  );
}

function VideoPreview({ src, className }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <video
        src={src}
        className="h-full w-full object-contain object-center"
        muted
        playsInline
        preload="metadata"
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#010064] shadow">
          <Icon name="Play" size={20} className="ml-0.5" />
        </span>
      </span>
    </div>
  );
}

/**
 * Carte fil d’annonces HubUP (alignée sur hubupfront NotificationRow, mode déplié).
 */
export default function HubupNotificationFeedCardPreview({
  feedName,
  groupIsPro = false,
  groupIconUrl,
  topicIconUrl,
  feedIcon,
  title,
  bodyHtml,
  linkUrl,
  mediaItems = [],
  priority = 'normal',
  hasForm = false,
  groupFeedFont,
  groupColorHexBg,
  groupColorHexFont,
}) {
  const sizeClasses = HUBUP_NOTIF_FEED_SIZE_MD;
  const hasRichBody = Boolean(String(bodyHtml || '').trim());
  const brand = notificationFeedBrandStyles({
    groupFeedFont,
    groupColorHexBg,
    groupColorHexFont,
  });

  const defaultCardUnread = 'bg-[#EEF1FA] ring-1 ring-[#010064]/20';
  const chromeGap = 'gap-2.5 sm:gap-3';
  const mediaClass =
    'max-h-96 w-full rounded-md border border-[#E8E4FF] bg-[#F7F6F0]/80 object-contain object-center shadow-sm';

  return (
    <>
      {brand.active && groupFeedFont ? (
        <HubupGroupFeedFontLoader font={groupFeedFont} />
      ) : null}
      <div
        role="article"
        className={`
          group relative w-full min-w-0 max-w-full overflow-x-hidden rounded-2xl border border-[#E8E4FF] px-5 py-3 pl-2.5 shadow-sm
          ${brand.active ? 'ring-1 ring-[#010064]/15' : defaultCardUnread}
        `}
        style={brand.cardStyle}
      >
        <div className={`grid min-w-0 grid-cols-[2.5rem_1fr] items-start ${chromeGap}`}>
          <div className="relative row-start-1 shrink-0">
            <FeedTopicAvatar
              groupIconUrl={groupIconUrl}
              topicIconUrl={topicIconUrl}
              feedIcon={feedIcon}
              size="md"
            />
            <span
              className="absolute -left-0.5 -top-0.5 z-10 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#010064] shadow-sm"
              aria-hidden
            />
          </div>

          <div className="row-start-1 min-w-0">
            <div className="flex items-start justify-between gap-2 sm:gap-3 mb-1">
              <div className="flex min-w-0 flex-1 items-center gap-2 flex-wrap">
                <span
                  className={`${sizeClasses.feedName} inline-flex min-w-0 max-w-full items-center gap-1 break-words font-bold text-[#333483]`}
                  style={brand.active ? brand.textStyle : undefined}
                >
                  <span className="min-w-0 break-words">{feedName}</span>
                  {groupIsPro ? <ProVerifiedBadge className="mt-px" size={13} /> : null}
                </span>
                {priority === 'high' ? (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                    🔥 Priorité haute
                  </span>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-0.5">
                <span className="inline-flex items-center gap-1 rounded-md px-0.5 py-0.5 text-[#333483]">
                  <Heart size={14} strokeWidth={2} className="text-[#333483]" />
                  <span className="text-xs font-mono tabular-nums leading-none">0</span>
                </span>
                <div className="flex items-center gap-1 text-[#333483]">
                  <Clock size={11} className="shrink-0" />
                  <span className="text-xs font-mono tabular-nums leading-none">à l&apos;instant</span>
                </div>
              </div>
            </div>

            <p
              className={`${sizeClasses.title} break-words font-bold text-[#1E1B4B]`}
              style={brand.active ? brand.textStyle : undefined}
            >
              {sanitizeNotificationDisplayText(title || '')}
            </p>

            {hasRichBody ? (
              <div
                className={`${sizeClasses.rich} relative mt-1 min-w-0 overflow-x-hidden text-[#333483]/90 [&_img]:my-2 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md`}
                style={brand.active ? brand.contentFontStyle : undefined}
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichBodyHtmlForPreview(bodyHtml),
                }}
              />
            ) : null}

            {mediaItems.length > 0 ? (
              <div className="mt-2 flex w-full max-w-2xl flex-col gap-2">
                {mediaItems.map((item, i) => {
                  if (item.kind === 'pdf') {
                    return (
                      <NotificationPdfCard
                        key={item.key || i}
                        label={item.label}
                        compact={mediaItems.length === 1}
                      />
                    );
                  }
                  if (item.kind === 'video') {
                    return (
                      <VideoPreview
                        key={item.key || i}
                        src={item.src}
                        className={`${mediaClass} max-h-96`}
                      />
                    );
                  }
                  return (
                    <img
                      key={item.key || i}
                      src={item.src}
                      alt=""
                      className={mediaClass}
                    />
                  );
                })}
              </div>
            ) : null}

            {linkUrl?.trim() ? (
              <span className="mt-2 block text-xs font-bold text-[#333483]">
                Ouvrir le lien →
              </span>
            ) : null}
          </div>

          {hasForm ? (
            <>
              <div className="row-start-2 mt-2 self-center">
                <FeedFormAvatar size="md" />
              </div>
              <div className="row-start-2 mt-2 min-w-0 self-center">
                <span className="block text-xs font-bold text-[#010064] underline">
                  accéder au formulaire
                </span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
